import "@/styles/components/forms/components-add-files.scss";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpFromBracket,
  faXmark,
  faCheck,
} from "@fortawesome/sharp-regular-svg-icons";

export type ConvertedFile = {
  fileName: string;
  files: File[];
};

export type ConversionResponse = {
  successfulConversions: ConvertedFile[];
  conversionFailedFilenames: string[]; // the filenames of the files that errored outs
};

export default function UploadDragDrop({
  sectionHeader = "Upload Files",
  headerText = "Select files to upload",
  subText = "Drag your files here...",
  onDragDropAction = (
    _files: File[],
  ): Promise<ConversionResponse | undefined> => Promise.resolve(undefined), // do nothing if no function passed in
  onBadItemUpload = (_errors: string[]) => {}, // do nothing if no function passed in
}) {
  const [files, setFiles] = useState<File[]>([]); // Store uploaded files in component state

  const validExtensions = ["pdf", "jpg", "png"];

  const createErrorMessages = (rejections: any[]): string[] => {
    let tooManyFilesError = false;
    const tooManyFilesRejected: string[] = [];
    let fileTypeInvalidError = false;
    const fileTypeInvalidRejected: string[] = [];
    let fileTooLargeError = false;
    const fileTooLargeRejectd: string[] = [];
    rejections.forEach((reject: { errors: any[]; file: { name: string } }) => {
      reject.errors.forEach((error: { code: string }) => {
        if (error.code === "too-many-files") {
          tooManyFilesError = true;
          tooManyFilesRejected.push(reject.file.name);
        } else if (error.code === "file-invalid-type") {
          fileTypeInvalidError = true;
          fileTypeInvalidRejected.push(reject.file.name);
        } else if (error.code === "file-too-large") {
          fileTooLargeError = true;
          fileTooLargeRejectd.push(reject.file.name);
        }
      });
    });

    const errorMessages: string[] = [];
    const tooManyFilesErrorMessage = `Too many files uploaded, the following files were not uploaded: ${tooManyFilesRejected}.\n`;
    const fileTypeInvalidErrorMessage = `Files must be of type: ${validExtensions}. The following files were not uploaded: ${fileTypeInvalidRejected}.\n`;
    const fileTooLargeErrorMessage = `File too large, the max file size is 20mb, the following files were not uploaded: ${fileTooLargeRejectd}.\n`;
    if (tooManyFilesError) {
      errorMessages.push(tooManyFilesErrorMessage);
    }
    if (fileTypeInvalidError) {
      errorMessages.push(fileTypeInvalidErrorMessage);
    }
    if (fileTooLargeError) {
      errorMessages.push(fileTooLargeErrorMessage);
    }
    if (errorMessages.length === 0) {
      errorMessages.push("Failed to upload files.");
    }
    return errorMessages;
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (acceptedFiles: File[]) => {
      const newFiles: File[] = [...files, ...acceptedFiles];
      setFiles(newFiles);
      const conversionResponse = await onDragDropAction(newFiles);
      if (conversionResponse) {
        const passingFiles = newFiles.filter(
          (newfile) =>
            !conversionResponse.conversionFailedFilenames.includes(
              newfile.name,
            ),
        );
        setFiles(passingFiles);
      }
    },
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "application/pdf": [],
    },
    maxSize: 20971520,
    onDropRejected: (rejections) => {
      const errorMessages = createErrorMessages(rejections);
      onBadItemUpload(errorMessages);
    },
  });

  const handleDeleteFile = (file: File) => {
    // Remove the file from the state
    const updatedFiles = files.filter((f) => f !== file);
    setFiles(updatedFiles);
    onDragDropAction(updatedFiles);
  };

  const fileItems = files.map((file) => (
    <div className="uploaded_file" key={file.name}>
      <div className="status_indicator">
        <FontAwesomeIcon icon={faCheck} title="check" />
      </div>
      <div className="file_details">
        <p className="file_name">{file.name}</p>
        <p className="file_size">
          {file.size}
          {" bytes"}
        </p>
      </div>
      <div className="controls">
        <FontAwesomeIcon
          icon={faXmark}
          onClick={() => handleDeleteFile(file)}
          title="x"
        />
      </div>
    </div>
  ));

  return (
    <div className="upload_content">
      <div className="upload_drag_drop_outer_container">
        <h3>{sectionHeader}</h3>

        <div className="uploaded_files_container">{fileItems}</div>

        <div {...getRootProps({ className: "dropzone" })}>
          <input {...getInputProps()} />
          <div
            className="upload_drag_drop_inner_container"
            data-testid="drop-input"
          >
            <div className="upload_icon">
              <FontAwesomeIcon
                className="fa-sharp"
                icon={faArrowUpFromBracket}
                title="Upload"
              />
            </div>
            <p className="action_header">{headerText}</p>
            <p className="action_subtext">{subText}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

UploadDragDrop.propTypes = {
  sectionHeader: PropTypes.string,
  headerText: PropTypes.string,
  subText: PropTypes.string,
  numOfFiles: PropTypes.number,
  onDragDropAction: PropTypes.func,
  onBadItemUpload: PropTypes.func,
};
